// Tenant-slug resolution helpers for the login surface.
//
// Mirrors the backend's middleware.TenantSlugFromHost: the same
// reserved-subdomains list, the same "first label wins" extraction.
// Keeping the rules in lockstep means a request that the backend
// would resolve from Host won't end up with a different slug than the
// frontend predicted — important for the "tenant detected, hide
// the field" UX.

const RESERVED_SUBDOMAINS = new Set<string>([
  '',
  'www',
  'app',
  'api',
  'admin',
  'platform',
  'localhost',
]);

const SLUG_RE = /^[a-z][a-z0-9_]{1,29}$/;

/**
 * Pull a tenant slug out of `window.location.hostname` when it's of the
 * form `<slug>.<root-domain>`. Returns null when:
 *   - the hostname has no dot at all (localhost, 127.0.0.1)
 *   - the first label is reserved (www, app, api, ...)
 *   - the first label fails the slug regex
 *   - the hostname is an IP literal
 *
 * Callers MUST treat this as a hint, not authority — the backend has
 * the same logic and will reject mismatches at login time.
 */
export function detectTenantSlugFromHost(hostname: string): string | null {
  if (!hostname) return null;

  // IP literals — never a tenant slug.
  if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) return null;
  if (hostname.includes(':') && hostname.startsWith('[')) return null;

  const lowered = hostname.toLowerCase();
  const dot = lowered.indexOf('.');
  if (dot < 0) return null; // single-label hosts: localhost, etc.
  const first = lowered.slice(0, dot);
  if (RESERVED_SUBDOMAINS.has(first)) return null;
  if (!SLUG_RE.test(first)) return null;
  return first;
}

/**
 * Persist a tenant slug across page reloads on root-domain deploys.
 * Subdomain deploys never need this — the slug is in the URL.
 */
const TENANT_KEY = 'dsshield_tenant_slug';

export function persistTenantSlug(slug: string): void {
  if (slug) localStorage.setItem(TENANT_KEY, slug);
}

export function getPersistedTenantSlug(): string | null {
  try {
    return localStorage.getItem(TENANT_KEY);
  } catch {
    return null;
  }
}

export function clearPersistedTenantSlug(): void {
  try {
    localStorage.removeItem(TENANT_KEY);
  } catch {
    // ignore
  }
}
